import React, {useRef} from 'react'
import PropTypes from 'prop-types'
import {Box, Text, useStyleConfig, useMediaQuery} from '@chakra-ui/react'
import {Navigation, Pagination, Keyboard /*, Mousewheel*/} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'
import PromoSlideImageCard from './partials/promo-slide-image-card'
import {ChevronRight, ChevronLeft} from '../../icons'
import {useIntl} from 'react-intl'
import {remCalc} from '../../../theme/_utils'

export const PromoSliderImage = ({heading, sliderGroup, uid}) => {
    const swiperStyles = useStyleConfig('Swiper')
    const styles = useStyleConfig('PromoSlider')
    const swiperRef = useRef(null)
    const intl = useIntl()

    const [isSmallTablet] = useMediaQuery(
        `(min-width: ${remCalc(450)}) and (max-width: ${remCalc(767)})`,
    )

    const slidesToView = {
        base: 1,
        sm: 1.3,
        md: 1.6,
        lg: 1.7,
        xl: 1.9,
    }

    if (!sliderGroup || sliderGroup?.length === 0) return null

    // swiperjs doesn't work correctly with 3 slides when loop and centeredSlides are enabled/
    // I add duplicates and hide the bullets from the fake slide
    const slides = [...sliderGroup]
    const needsFakeSlides = sliderGroup.length === 3
    const allSlides = needsFakeSlides
        ? [...slides, ...slides.map((slide) => ({...slide, isFake: true}))]
        : slides

    return (
        <Box
            id={`promo-slider-image-${uid}`}
            __css={{...swiperStyles, ...styles.container}}
            data-testid="promo-slider-image-container"
        >
            {heading && (
                <Text as="h2" {...styles.heading}>
                    {heading}
                </Text>
            )}
            <Swiper
                aria-label={intl.formatMessage({
                    id: 'label.promo.swiper.slider',
                    defaultMessage: 'Swiper',
                })}
                modules={[Navigation, Pagination, Keyboard]}
                navigation={{
                    prevEl: `.swiper-button-prev`,
                    nextEl: `.swiper-button-next`,
                }}
                pagination={{
                    clickable: true,
                    el: `#promo-slider-image-${uid} .promo-slider-pagination`,
                    renderBullet: (index, className) => {
                        if (index < slides.length) {
                            const changeSlideLabel = intl.formatMessage({
                                id: 'label.promo.swiper.slider.image',
                                defaultMessage: 'Change slide',
                            })
                            return `<button type="button" class="${className}" aria-label="${changeSlideLabel}" aria-controls="promo-slider-image-${uid}" data-slide-index="${index}"></button>`
                        }
                        return ''
                    },
                }}
                keyboard={{
                    enabled: true,
                }}
                // mousewheel={true}
                loop={true}
                centeredSlides={true}
                tabIndex={0}
                onInit={(swiper) => {
                    swiperRef.current = swiper
                    const bullets = document.querySelectorAll(
                        `#promo-slider-image-${uid} .swiper-pagination-bullet`,
                    )
                    bullets.forEach((bullet, idx) => {
                        bullet.setAttribute('tabindex', '0')
                        if (idx === swiper.realIndex % slides.length) {
                            bullet.setAttribute('aria-current', 'true')
                        } else {
                            bullet.removeAttribute('aria-current')
                        }
                    })
                }}
                onSlideChange={(swiper) => {
                    // Determine the real index (exclude fake slides)
                    const realIndex = swiper.realIndex % slides.length
                    const bullets = document.querySelectorAll(
                        `#promo-slider-image-${uid} .swiper-pagination-bullet`,
                    )
                    bullets.forEach((bullet, idx) => {
                        if (idx === realIndex) {
                            bullet.classList.add('swiper-pagination-bullet-active')
                            bullet.setAttribute('aria-current', 'true')
                        } else {
                            bullet.classList.remove('swiper-pagination-bullet-active')
                            bullet.removeAttribute('aria-current')
                        }
                    })
                }}
                breakpoints={{
                    0: {
                        slidesPerView: slidesToView.base,
                        spaceBetween: 0,
                    },
                    450: {
                        slidesPerView: slidesToView.sm,
                        spaceBetween: 16,
                    },
                    768: {
                        slidesPerView: slidesToView.md,
                        spaceBetween: 24,
                    },
                    1024: {
                        slidesPerView: slidesToView.lg,
                        spaceBetween: 75,
                    },
                    1200: {
                        slidesPerView: slidesToView.xl,
                        spaceBetween: 142,
                    },
                }}
            >
                {allSlides.map((slider, index) => (
                    <SwiperSlide key={index} data-fake={slider.isFake ? 'true' : 'false'}>
                        <PromoSlideImageCard
                            title={slider.text_block.title}
                            bodyCopy={slider.text_block.body_copy}
                            button={slider.text_block.button}
                            url={slider?.media?.url}
                            alt={slider?.media?.description}
                            images={slider.images}
                        />
                    </SwiperSlide>
                ))}
                {/* note chevronIcon is used all swipers thats why top being overwritten*/}
                <ChevronLeft
                    className="swiper-button-prev"
                    aria-label={intl.formatMessage({
                        id: 'label.promo.swiper.button.prev',
                        defaultMessage: 'swiper button previous icon',
                    })}
                    tabIndex={0}
                    sx={{...styles.chevronIcon, top: '50%'}}
                    left={isSmallTablet ? '9.5%' : {base: '4%', md: '18%', lg: '19%', xl: '21%'}}
                />
                <ChevronRight
                    className="swiper-button-next"
                    aria-label={intl.formatMessage({
                        id: 'label.promo.swiper.button.next',
                        defaultMessage: 'swiper button next icon',
                    })}
                    tabIndex={0}
                    sx={{...styles.chevronIcon, top: '50%'}}
                    right={isSmallTablet ? '9.5%' : {base: '4%', md: '18%', lg: '19%', xl: '21%'}}
                />
                <Box
                    className="promo-slider-pagination"
                    role="group"
                    aria-label={intl.formatMessage({
                        id: 'label.promo.swiper.pagination',
                        defaultMessage: `Pagination for promo slider`,
                    })}
                />
            </Swiper>
        </Box>
    )
}

PromoSliderImage.propTypes = {
    heading: PropTypes.string,
    uid: PropTypes.string,
    sliderGroup: PropTypes.arrayOf(
        PropTypes.shape({
            text_block: PropTypes.shape({
                title: PropTypes.string,
                body_copy: PropTypes.string,
                cta: PropTypes.shape({title: PropTypes.string, href: PropTypes.string}),
                is_external_url: PropTypes.bool,
            }),
            media: PropTypes.shape({
                url: PropTypes.string,
                alt: PropTypes.string,
            }),
        }),
    ),
}
